/**
 * MatcherExplainerVideo
 * ─────────────────────────────────────────────────────────────
 * Reusable lightbox for the "How the matcher works" explainer.
 *
 * Rendered by both:
 *   - `MatcherPromptCard` (across many pages) via "How it works (60s)" link
 *   - `MiniMatcher` first screen via "Watch how it works" toggle
 *
 * Video sourced from Google Drive. Hardened against download/sharing:
 *   - iframe positioned past the container's right edge so the Drive
 *     pop-out button gets clipped by `overflow-hidden`
 *   - `onContextMenu` prevented (blocks right-click download menu)
 *   - `rm=minimal` embed parameter for stripped-back Drive chrome
 *   - No direct URL / download link exposed anywhere in markup
 */
import { useEffect, useCallback, useState } from 'react';
import { X, Play } from 'lucide-react';

const VIDEO_ID = '102VAEri64zksOqtTHac2ixwp56LrhF2x';
// 2026-02-28: autoplay + minimal chrome. Drive still requires the user's
// browser to allow autoplay (typically requires muted or a prior user
// gesture). Since the lightbox is *always* opened by an explicit click,
// browsers treat this as user-initiated and allow sound.
const EMBED_URL = `https://drive.google.com/file/d/${VIDEO_ID}/preview?rm=minimal&autoplay=1`;

/** The lightbox modal itself. */
export const MatcherExplainerVideoModal = ({ open, onClose }) => {
  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [open, handleKey]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="How the matcher works"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="matcher-explainer-modal"
    >
      <div
        className="relative w-full max-w-3xl rounded-2xl overflow-hidden bg-black shadow-2xl ring-1 ring-white/10"
        style={{ aspectRatio: '16 / 9' }}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <iframe
          src={EMBED_URL}
          title="How the BuildPilot matcher works"
          allow="autoplay; encrypted-media; fullscreen"
          allowFullScreen
          className="absolute top-0 border-0"
          style={{ right: '-56px', width: 'calc(100% + 56px)', height: '100%' }}
          data-testid="matcher-explainer-iframe"
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close video"
          className="absolute top-3 left-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
          data-testid="matcher-explainer-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/** Small trigger link. Consistent visual across every placement. */
export const MatcherExplainerTrigger = ({
  onClick,
  variant = 'default',
  className = '',
  label = 'How it works',
  duration = '60s',
  testId = 'matcher-explainer-trigger',
}) => {
  const base =
    'inline-flex items-center gap-1.5 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#1d8efe]/40 rounded-full';
  const variants = {
    default:
      'text-[13px] text-[#1d8efe] hover:text-[#1670cc] px-2 py-1',
    ghostLight:
      'text-[12.5px] text-white/70 hover:text-white px-3 py-1.5 bg-white/5 hover:bg-white/10',
    ghostDark:
      'text-[12.5px] text-[#424245] hover:text-[#1d1d1f] px-3 py-1.5 bg-[#f5f5f7] hover:bg-[#e8e8ed]',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${variants[variant] || variants.default} ${className}`}
      data-testid={testId}
    >
      <Play className="w-3 h-3" fill="currentColor" />
      <span>
        {label}
        {duration ? <span className="opacity-70"> · {duration}</span> : null}
      </span>
    </button>
  );
};

/**
 * <HowItWorksButton /> - self-contained "How it works" button that manages
 * its own modal open state. Drop it onto any shortlist / matcher surface
 * without wiring up state. Plays the canonical 60s matcher explainer.
 */
export const HowItWorksButton = ({
  variant = 'default',
  className = '',
  label = 'How it works',
  duration = '60s',
  testId = 'how-it-works-button',
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <MatcherExplainerTrigger
        onClick={() => setOpen(true)}
        variant={variant}
        className={className}
        label={label}
        duration={duration}
        testId={testId}
      />
      <MatcherExplainerVideoModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default MatcherExplainerVideoModal;